import React, { useState, useMemo } from 'react';
import './DimensionSelector.css';

/**
 * DimensionSelector - A two-step selector for table dimensions
 * Step 1: Select depth (width) via tabs
 * Step 2: Select length from a visual grid with prices
 */
const DimensionSelector = ({ step, configuration, onSelect }) => {
    const selectedValue = configuration[step.key];

    // Parse options to extract unique depths and organize by depth
    const { depths, optionsByDepth } = useMemo(() => {
        const depthSet = new Set();
        const byDepth = {};

        step.options.forEach(option => {
            // Parse dimension from label like "22" x 24"" or value like "22x24"
            const match = option.label.match(/(\d+)"?\s*x\s*(\d+)/);
            if (match) {
                const depth = match[1];
                const length = match[2];

                depthSet.add(depth);
                if (!byDepth[depth]) {
                    byDepth[depth] = [];
                }
                byDepth[depth].push({
                    ...option,
                    depth,
                    length,
                    displayLength: `${length}"`
                });
            }
        });

        // Sort depths numerically
        const sortedDepths = Array.from(depthSet).sort((a, b) => parseInt(a) - parseInt(b));

        // Sort lengths within each depth
        Object.keys(byDepth).forEach(depth => {
            byDepth[depth].sort((a, b) => parseInt(a.length) - parseInt(b.length));
        });

        return { depths: sortedDepths, optionsByDepth: byDepth };
    }, [step.options]);

    // Determine selected depth from current value or default to first
    const getSelectedDepth = () => {
        if (selectedValue) {
            const match = selectedValue.match(/^(\d+)/);
            if (match && depths.includes(match[1])) {
                return match[1];
            }
        }
        return depths[0] || null;
    };

    const [activeDepth, setActiveDepth] = useState(getSelectedDepth);

    // Update active depth when depths change (e.g., when options are filtered)
    React.useEffect(() => {
        if (depths.length > 0 && !depths.includes(activeDepth)) {
            setActiveDepth(depths[0]);
        }
    }, [depths, activeDepth]);

    const handleDepthChange = (depth) => {
        setActiveDepth(depth);
        // Store the frame depth in configuration when user switches tabs
        onSelect('frame_depth', depth);
        // Clear table_size selection when depth changes to prevent mismatch
        if (selectedValue) {
            const currentDepth = selectedValue.split('x')[0];
            if (currentDepth !== depth) {
                onSelect(step.key, null);
            }
        }
    };

    const handleLengthSelect = (option) => {
        onSelect(step.key, option.value);
        // Ensure frame_depth is always set to match the selected top depth
        onSelect('frame_depth', option.depth);
    };

    const currentOptions = optionsByDepth[activeDepth] || [];

    if (depths.length === 0) {
        return (
            <div className="dimension-selector">
                <p className="step-description">{step.description}</p>
                <p className="no-options">No size options available for current selection.</p>
            </div>
        );
    }

    return (
        <div className="dimension-selector">
            <p className="step-description">{step.description}</p>

            {/* Depth Tabs */}
            <div className="depth-selector">
                <label className="depth-label">Table Depth:</label>
                <div className="depth-tabs">
                    {depths.map(depth => (
                        <button
                            key={depth}
                            type="button"
                            className={`depth-tab ${activeDepth === depth ? 'active' : ''}`}
                            onClick={() => handleDepthChange(depth)}
                        >
                            {depth}"
                        </button>
                    ))}
                </div>
            </div>

            {/* Length Grid */}
            <div className="length-selector">
                <label className="length-label">Table Length:</label>
                <div className="length-grid">
                    {currentOptions.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={`length-option ${selectedValue === option.value ? 'selected' : ''}`}
                            onClick={() => handleLengthSelect(option)}
                        >
                            <span className="length-value">{option.displayLength}</span>
                            <span className="length-price">
                                {option.price ? `$${option.price.toLocaleString()}` : 'Included'}
                            </span>
                            {option.sku && (
                                <span className="length-sku">{option.sku}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Summary of selection */}
            {selectedValue && (
                <div className="selection-summary">
                    <span className="summary-label">Selected:</span>
                    <span className="summary-value">
                        {step.options.find(o => o.value === selectedValue)?.label}
                    </span>
                </div>
            )}
        </div>
    );
};

export default DimensionSelector;
